// src/pages/StaffSettings.js
//
// Central control panel for the per-staff flags that drive behavior across the
// whole app. Everything written here lands on users/{uid} using the exact same
// field names the Staff page and the Cloud Functions read, so a change made on
// this page takes effect everywhere immediately.
//
// Controlled from here:
//   - Hide From Team Message           -> hideFromTeamMessaging
//   - Receive Vehicle Expiration Emails-> notificationPrefs.vehicles
//   - Receive Job Schedule Emails      -> notificationPrefs.jobSchedule
//   - Receive Job Update Emails        -> notificationPrefs.jobUpdates
//   - Show In Dropdowns                -> hideFromScheduling (inverted)
//
// Also editable inline: Employee / Contractor (workerType) and Status.

import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  doc,
  onSnapshot,
  updateDoc,
  writeBatch,
} from "firebase/firestore";
import { db } from "../firebaseConfig";
import {
  STAFF_SETTING_FIELDS,
  STAFF_SETTING_KEYS,
  buildStaffSettingsPatch,
  getStaffDisplayName,
  isStaffDeactivated,
  isStaffSettingUnset,
  resolveStaffSettings,
} from "../utils/staffSettingsUtils";
import "./StaffSettings.css";

const WORKER_TYPES = ["Employee", "Sub Contractor"];
const STATUS_VALUES = ["Active", "Deactivated"];

const normalizeWorkerType = (value = "") => {
  const raw = String(value || "").trim().toLowerCase();
  if (!raw) return "Employee";
  if (raw.includes("sub") || raw.includes("contract")) return "Sub Contractor";
  return "Employee";
};

const normalizeStatus = (user = {}) =>
  isStaffDeactivated(user) ? "Deactivated" : "Active";

const StaffSettings = () => {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  // rowId -> { ...settings, workerType, status }
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState("");
  const [saveError, setSaveError] = useState("");

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("Active");
  const [typeFilter, setTypeFilter] = useState("All");
  const [sortConfig, setSortConfig] = useState({
    key: "name",
    direction: "asc",
  });

  // ---------------------------------------------------------------- load ---

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, "users"),
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setStaff(list);
        setLoading(false);
        setLoadError("");
      },
      (err) => {
        console.error("StaffSettings: users listener failed", err);
        setLoadError(err?.message || "Unable to load staff.");
        setLoading(false);
      }
    );

    return () => unsub();
  }, []);

  // -------------------------------------------------------------- derived --

  // Saved (server) state for a row, normalized into the UI shape.
  const savedRowState = useMemo(() => {
    const map = {};
    staff.forEach((member) => {
      map[member.id] = {
        ...resolveStaffSettings(member),
        workerType: normalizeWorkerType(member.workerType),
        status: normalizeStatus(member),
      };
    });
    return map;
  }, [staff]);

  // What the row currently shows = saved state overlaid with unsaved edits.
  const rowState = (id) => ({
    ...(savedRowState[id] || {}),
    ...(drafts[id] || {}),
  });

  const isRowDirty = (id) => {
    const draft = drafts[id];
    if (!draft) return false;
    const saved = savedRowState[id] || {};
    return Object.keys(draft).some((key) => draft[key] !== saved[key]);
  };

  const dirtyIds = useMemo(
    () => Object.keys(drafts).filter((id) => isRowDirty(id)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [drafts, savedRowState]
  );

  const visibleStaff = useMemo(() => {
    const term = search.trim().toLowerCase();

    const filtered = staff.filter((member) => {
      const state = rowState(member.id);

      if (statusFilter !== "All" && state.status !== statusFilter) return false;
      if (typeFilter !== "All" && state.workerType !== typeFilter) return false;

      if (!term) return true;

      const haystack = [
        getStaffDisplayName(member),
        member.email || "",
        member.position || "",
        member.role || "",
      ]
        .join(" ")
        .toLowerCase();

      return haystack.includes(term);
    });

    const direction = sortConfig.direction === "asc" ? 1 : -1;

    return [...filtered].sort((a, b) => {
      const stateA = rowState(a.id);
      const stateB = rowState(b.id);

      let valueA;
      let valueB;

      if (sortConfig.key === "name") {
        valueA = getStaffDisplayName(a);
        valueB = getStaffDisplayName(b);
      } else if (sortConfig.key === "workerType" || sortConfig.key === "status") {
        valueA = stateA[sortConfig.key] || "";
        valueB = stateB[sortConfig.key] || "";
      } else {
        // boolean setting column: Yes sorts before No when ascending
        valueA = stateA[sortConfig.key] ? "0" : "1";
        valueB = stateB[sortConfig.key] ? "0" : "1";
      }

      const compared = String(valueA).localeCompare(String(valueB), undefined, {
        numeric: true,
        sensitivity: "base",
      });

      if (compared !== 0) return compared * direction;

      return getStaffDisplayName(a).localeCompare(getStaffDisplayName(b));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staff, drafts, savedRowState, search, statusFilter, typeFilter, sortConfig]);

  // --------------------------------------------------------------- edits ---

  const setRowValue = (id, key, value) => {
    setDrafts((prev) => ({
      ...prev,
      [id]: {
        ...(prev[id] || {}),
        [key]: value,
      },
    }));
    setSaveError("");
    setSavedAt("");
  };

  // Column bulk set: applies to every row currently visible.
  const setColumnForVisible = (key, value) => {
    if (!visibleStaff.length) return;

    setDrafts((prev) => {
      const next = { ...prev };
      visibleStaff.forEach((member) => {
        next[member.id] = {
          ...(next[member.id] || {}),
          [key]: value,
        };
      });
      return next;
    });
    setSaveError("");
    setSavedAt("");
  };

  const discardChanges = () => {
    setDrafts({});
    setSaveError("");
    setSavedAt("");
  };

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const sortIndicator = (key) => {
    if (sortConfig.key !== key) return "";
    return sortConfig.direction === "asc" ? " \u25B2" : " \u25BC";
  };

  // ---------------------------------------------------------------- save ---

  const buildRowPayload = (id) => {
    const state = rowState(id);

    const payload = {
      ...buildStaffSettingsPatch(state),
      workerType: state.workerType,
      status: state.status,
      isActive: state.status !== "Deactivated",
    };

    if (state.status === "Deactivated") {
      const member = staff.find((s) => s.id === id);
      if (!member?.deactivatedAt) {
        payload.deactivatedAt = new Date().toISOString();
      }
    }

    return payload;
  };

  const saveAll = async () => {
    if (!dirtyIds.length || saving) return;

    setSaving(true);
    setSaveError("");

    try {
      // writeBatch caps at 500 operations; chunk so a large roster still saves.
      const chunks = [];
      for (let i = 0; i < dirtyIds.length; i += 400) {
        chunks.push(dirtyIds.slice(i, i + 400));
      }

      for (const chunk of chunks) {
        const batch = writeBatch(db);
        chunk.forEach((id) => {
          batch.update(doc(db, "users", id), buildRowPayload(id));
        });
        // eslint-disable-next-line no-await-in-loop
        await batch.commit();
      }

      setDrafts({});
      setSavedAt(new Date().toLocaleTimeString());
    } catch (err) {
      console.error("StaffSettings: save failed", err);
      setSaveError(err?.message || "Unable to save changes.");
    } finally {
      setSaving(false);
    }
  };

  const saveRow = async (id) => {
    if (!isRowDirty(id) || saving) return;

    setSaving(true);
    setSaveError("");

    try {
      await updateDoc(doc(db, "users", id), buildRowPayload(id));
      setDrafts((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      setSavedAt(new Date().toLocaleTimeString());
    } catch (err) {
      console.error("StaffSettings: row save failed", err);
      setSaveError(err?.message || "Unable to save changes.");
    } finally {
      setSaving(false);
    }
  };

  // Writes the currently displayed value for every setting on every staff
  // record that has no explicit value stored yet, so nothing is left implicit.
  const seedUnsetDefaults = async () => {
    const targets = staff.filter((member) =>
      STAFF_SETTING_KEYS.some((key) => isStaffSettingUnset(member, key))
    );

    if (!targets.length) {
      window.alert("Every staff record already has all settings stored.");
      return;
    }

    const confirmed = window.confirm(
      `${targets.length} staff record(s) are missing one or more stored settings.\n\n` +
        "Write the currently shown values to those records?\n\n" +
        "This does not change any behavior. It only makes the current defaults explicit."
    );
    if (!confirmed) return;

    setSaving(true);
    setSaveError("");

    try {
      const chunks = [];
      for (let i = 0; i < targets.length; i += 400) {
        chunks.push(targets.slice(i, i + 400));
      }

      for (const chunk of chunks) {
        const batch = writeBatch(db);
        chunk.forEach((member) => {
          batch.update(
            doc(db, "users", member.id),
            buildStaffSettingsPatch(rowState(member.id))
          );
        });
        // eslint-disable-next-line no-await-in-loop
        await batch.commit();
      }

      setSavedAt(new Date().toLocaleTimeString());
    } catch (err) {
      console.error("StaffSettings: seeding defaults failed", err);
      setSaveError(err?.message || "Unable to write defaults.");
    } finally {
      setSaving(false);
    }
  };

  // ---------------------------------------------------------------- view ---

  const summaryCounts = useMemo(() => {
    const counts = {};
    STAFF_SETTING_KEYS.forEach((key) => {
      counts[key] = visibleStaff.filter((m) => rowState(m.id)[key]).length;
    });
    return counts;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleStaff, drafts, savedRowState]);

  return (
    <div className="stset-page">
      <div className="stset-header">
        <div>
          <h2 className="stset-title">Staff Settings</h2>
          <p className="stset-subtitle">
            One place to control team messaging visibility, dropdown visibility,
            and email opt-ins for every staff member. Changes save to the same
            fields the Staff page and the email functions read, so they take
            effect across the whole site.
          </p>
        </div>

        <div className="stset-header-actions">
          <button
            type="button"
            className="stset-btn stset-btn-ghost"
            onClick={seedUnsetDefaults}
            disabled={saving || loading}
            title="Write the currently displayed values onto records that have no stored value yet."
          >
            Store Defaults
          </button>

          <button
            type="button"
            className="stset-btn stset-btn-ghost"
            onClick={discardChanges}
            disabled={!dirtyIds.length || saving}
          >
            Discard
          </button>

          <button
            type="button"
            className="stset-btn stset-btn-primary"
            onClick={saveAll}
            disabled={!dirtyIds.length || saving}
          >
            {saving
              ? "Saving..."
              : dirtyIds.length
              ? `Save ${dirtyIds.length} Change${dirtyIds.length === 1 ? "" : "s"}`
              : "Save"}
          </button>
        </div>
      </div>

      {loadError ? (
        <div className="stset-alert">Could not load staff: {loadError}</div>
      ) : null}

      {saveError ? (
        <div className="stset-alert">Could not save: {saveError}</div>
      ) : null}

      {savedAt && !dirtyIds.length ? (
        <div className="stset-notice">Saved at {savedAt}.</div>
      ) : null}

      <div className="stset-toolbar">
        <input
          className="stset-input"
          type="text"
          placeholder="Search name, email, position..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <label className="stset-field">
          <span>Status</span>
          <select
            className="stset-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="Active">Active</option>
            <option value="Deactivated">Deactivated</option>
            <option value="All">All</option>
          </select>
        </label>

        <label className="stset-field">
          <span>Type</span>
          <select
            className="stset-select"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <option value="All">All</option>
            <option value="Employee">Employee</option>
            <option value="Sub Contractor">Sub Contractor</option>
          </select>
        </label>

        <div className="stset-count">
          {loading
            ? "Loading..."
            : `${visibleStaff.length} shown${
                dirtyIds.length ? ` \u00B7 ${dirtyIds.length} unsaved` : ""
              }`}
        </div>
      </div>

      <div className="stset-table-wrap">
        <table className="stset-table">
          <thead>
            <tr>
              <th
                className="stset-sortable stset-col-name"
                onClick={() => handleSort("name")}
              >
                Staff Name{sortIndicator("name")}
              </th>
              <th
                className="stset-sortable"
                onClick={() => handleSort("workerType")}
              >
                Employee / Contractor{sortIndicator("workerType")}
              </th>
              <th
                className="stset-sortable"
                onClick={() => handleSort("status")}
              >
                Status{sortIndicator("status")}
              </th>

              {STAFF_SETTING_FIELDS.map((field) => (
                <th key={field.key} className="stset-setting-head">
                  <button
                    type="button"
                    className="stset-head-btn"
                    onClick={() => handleSort(field.key)}
                    title={field.help}
                  >
                    {field.label}
                    {sortIndicator(field.key)}
                  </button>

                  <div className="stset-bulk">
                    <button
                      type="button"
                      className="stset-bulk-btn"
                      onClick={() => setColumnForVisible(field.key, true)}
                      disabled={!visibleStaff.length || saving}
                      title={`Set every shown row to Yes (${field.yesMeans})`}
                    >
                      All Yes
                    </button>
                    <button
                      type="button"
                      className="stset-bulk-btn"
                      onClick={() => setColumnForVisible(field.key, false)}
                      disabled={!visibleStaff.length || saving}
                      title={`Set every shown row to No (${field.noMeans})`}
                    >
                      All No
                    </button>
                  </div>

                  <div className="stset-head-count">
                    {summaryCounts[field.key] || 0} of {visibleStaff.length} Yes
                  </div>
                </th>
              ))}

              <th className="stset-col-actions">Row</th>
            </tr>
          </thead>

          <tbody>
            {!loading && !visibleStaff.length ? (
              <tr>
                <td className="stset-empty" colSpan={STAFF_SETTING_FIELDS.length + 4}>
                  No staff match the current filters.
                </td>
              </tr>
            ) : null}

            {visibleStaff.map((member) => {
              const state = rowState(member.id);
              const dirty = isRowDirty(member.id);

              return (
                <tr
                  key={member.id}
                  className={dirty ? "stset-row stset-row-dirty" : "stset-row"}
                >
                  <td className="stset-col-name">
                    <div className="stset-name">
                      {getStaffDisplayName(member) || "(No name)"}
                    </div>
                    <div className="stset-sub">
                      {member.email || "No email on file"}
                    </div>
                    {!member.email ? (
                      <div className="stset-warn">
                        No email address, so email settings will not deliver.
                      </div>
                    ) : null}
                  </td>

                  <td>
                    <select
                      className="stset-select"
                      value={state.workerType}
                      onChange={(e) =>
                        setRowValue(member.id, "workerType", e.target.value)
                      }
                    >
                      {WORKER_TYPES.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </td>

                  <td>
                    <select
                      className="stset-select"
                      value={state.status}
                      onChange={(e) =>
                        setRowValue(member.id, "status", e.target.value)
                      }
                    >
                      {STATUS_VALUES.map((value) => (
                        <option key={value} value={value}>
                          {value}
                        </option>
                      ))}
                    </select>
                  </td>

                  {STAFF_SETTING_FIELDS.map((field) => {
                    const value = state[field.key] === true;
                    const unset = isStaffSettingUnset(member, field.key);

                    return (
                      <td key={field.key} className="stset-setting-cell">
                        <select
                          className={
                            value
                              ? "stset-select stset-yes"
                              : "stset-select stset-no"
                          }
                          value={value ? "yes" : "no"}
                          onChange={(e) =>
                            setRowValue(
                              member.id,
                              field.key,
                              e.target.value === "yes"
                            )
                          }
                          title={value ? field.yesMeans : field.noMeans}
                        >
                          <option value="yes">Yes</option>
                          <option value="no">No</option>
                        </select>

                        {unset && !drafts[member.id]?.[field.key] ? (
                          <span
                            className="stset-default-tag"
                            title="No value stored on this record yet. This is the app default."
                          >
                            default
                          </span>
                        ) : null}
                      </td>
                    );
                  })}

                  <td className="stset-col-actions">
                    <button
                      type="button"
                      className="stset-btn stset-btn-row"
                      onClick={() => saveRow(member.id)}
                      disabled={!dirty || saving}
                    >
                      {dirty ? "Save" : "Saved"}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="stset-legend">
        <h4 className="stset-legend-title">What each setting controls</h4>
        <ul className="stset-legend-list">
          {STAFF_SETTING_FIELDS.map((field) => (
            <li key={field.key}>
              <strong>{field.label}</strong> &mdash; {field.help}
            </li>
          ))}
        </ul>
        <p className="stset-legend-note">
          A row marked <em>default</em> has no value stored on that staff record
          yet, so the app default is being used. Use <strong>Store Defaults</strong>{" "}
          to write those values down explicitly. Staff with no email address will
          never receive email regardless of these settings.
        </p>
      </div>
    </div>
  );
};

export default StaffSettings;
